import { ArrowDown, ArrowUp, ArrowUpDown, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia } from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { TableHead } from "@/components/ui/table";
import { Link } from "@/i18n/link";
import { cn } from "@/lib/utils";

/** The frame around a table of a list: rounded, with a border, room at both ends of every row (the table cells only have a small padding, so the first and last cell get more) and taller rows, scrolling sideways when it is too wide (`tall` also scrolls it down under a head that stays in view). */
export function TableBox({ tall, children }: { tall?: boolean; children: ReactNode }) {
  return <div className={cn("overflow-auto border rounded-lg bg-card [&_th]:bg-card [&_thead_tr]:bg-card [&_th]:text-xs [&_th]:font-extrabold [&_th]:uppercase [&_th]:tracking-wider [&_td]:py-3 [&_td:first-child]:ps-4 [&_th:first-child]:ps-4 [&_td:last-child]:pe-4 [&_th:last-child]:pe-4", tall && "max-h-[70dvh] [&_[data-slot=table-container]]:overflow-visible [&_th]:sticky [&_th]:top-0 [&_th]:z-10")}>{children}</div>;
}

/** What a list shows when it has nothing: a round icon, one sentence and (when something can be done) the button that does it. */
export function EmptyState({ icon: Icon, text, action }: { icon: LucideIcon; text: string; action?: ReactNode }) {
  return (
    <Empty className="border rounded-lg">
      <EmptyHeader><EmptyMedia variant="icon"><Icon aria-hidden /></EmptyMedia><EmptyDescription>{text}</EmptyDescription></EmptyHeader>
      {action}
    </Empty>
  );
}

const ROWS = Array.from({ length: 8 }, (_, i) => i);

/** What a list screen shows while it loads: the title, the filter bar and rows of the table as grey bars. */
export function ListSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true">
      <div className="flex items-start justify-between gap-3"><div className="flex flex-col gap-2"><Skeleton className="h-7 w-48" /><Skeleton className="h-4 w-64" /></div><Skeleton className="h-9 w-32" /></div>
      <Skeleton className="h-12 w-full" />
      <div className="rounded-lg flex flex-col gap-2 border bg-card p-3">{ROWS.map((i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
    </div>
  );
}

/** The head of a column of a list that can be sorted: a link with the column's name (to `href`, the list sorted by it, a second click turns it round), with an arrow showing whether it is the column sorted by (`active`) and in which direction. */
export function SortHeader({ label, active, dir, href, className }: { label: string; active: boolean; dir: "asc" | "desc"; href: string; className?: string }) {
  const Icon = !active ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead className={className} aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : undefined}>
      <Link href={href} className={`inline-flex items-center gap-1 hover:text-foreground ${active ? "text-foreground" : ""}`}>{label}<Icon aria-hidden className={`size-3.5 ${active ? "" : "opacity-40"}`} /></Link>
    </TableHead>
  );
}

/** The sort of a list and the way to its other sorts: the column sorted by and its direction, the address of the list sorted another way, and the columns that start newest or biggest first. */
type ListSorting<C extends string> = { sort: C; dir: "asc" | "desc"; hrefFor: (sort: C, dir: "asc" | "desc") => string; descFirst?: readonly C[] };

/** The head of a sortable column: a link that sorts the list by it (a second click turns it round). */
export function SortHeadCell<C extends string>({ column, label, sorting, className }: { column: C; label: string; sorting: ListSorting<C>; className?: string }) {
  const active = sorting.sort === column;
  const first = sorting.descFirst?.includes(column) ? "desc" : "asc";
  const dir = active ? (sorting.dir === "asc" ? "desc" : "asc") : first;
  return <SortHeader label={label} active={active} dir={sorting.dir} href={sorting.hrefFor(column, dir)} className={className} />;
}

/** One chip of a legend: what it is called, what it means (its tooltip), its icon, the colours of its chip and how many things are in that state. */
type LegendItem = { id: string; label: string; help: string; icon: LucideIcon; chip: string; count: number };

/** The legend over a list: one square chip per state (icon, colour, name and how many), the same colours the rows have. Clicking a chip lists only those; clicking the chosen one again lists all of them. `hrefFor` gives the address for a state (or for none, `undefined`). */
export function StateLegend({ label, help, items, active, hrefFor, bare }: { bare?: boolean; label: string; help: string; items: LegendItem[]; active?: string; hrefFor: (id?: string) => string }) {
  return (
    <section aria-label={label} className={cn("flex flex-col gap-2", !bare && "border rounded-lg bg-card p-3")}>
      <p className="text-xs text-muted-foreground">{help}</p>
      <ul className="flex flex-wrap gap-2">
        {items.map(({ id, label: name, help: title, icon: Icon, chip, count }) => (
          <li key={id}>
            <Link href={hrefFor(active === id ? undefined : id)} aria-current={active === id ? "true" : undefined} title={title} className={`flex items-center gap-1.5 border rounded-md px-3 py-1 text-sm transition-shadow ${chip} ${active === id ? "ring-2 ring-ring ring-offset-1 ring-offset-background" : "hover:shadow-sm"}`}>
              <Icon aria-hidden className="size-4" /><span className="font-medium">{name}</span><span className="bg-background/70 px-1.5 text-xs tabular-nums">{count}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** What one state looks like in a list: its icon, the tint of its row (coloured edge at the start and a soft background), the colours of its chip and of its icon. */
export type Tone = { icon: LucideIcon; row: string; chip: string; iconColour: string };

/** The chips of a status legend: one per status, in order, with the icon and colours of its tone, its name, its tooltip and how many things are in it. */
export function buildLegendItems(ids: readonly string[], tones: Record<string, Tone>, counts: Record<string, number>, name: (id: string) => string, help: (id: string) => string): LegendItem[] {
  return ids.map((id) => ({ id, label: name(id), help: help(id), icon: tones[id].icon, chip: tones[id].chip, count: counts[id] ?? 0 }));
}

/** The status of a row as a small square chip with the state's icon and colours (the same as its legend chip and row edge). */
export function ToneBadge({ tone, label, className }: { tone: Tone; label: string; className?: string }) {
  const Icon = tone.icon;
  return <span className={cn("inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium", tone.chip, className)}><Icon aria-hidden className="size-3.5" />{label}</span>;
}
